import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { useListSurface } from '../../../../components/table/listSurface';
import { PostingStatusText } from '../components/PostingStatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatMoney } from '../../../../lib/formatMoney';
import { applicantsApi } from '../applicantsApi';
import { APPLICANT_AUDIT_ENTITY_TYPES, type JobPostingListItem } from '../applicantTypes';

/**
 * The Applicants module's index: every JOB POSTING — a plantilla item tagged for job posting —
 * and how many have applied to it. Opening one is where its applicants are recorded.
 *
 * Nothing is created HERE: a posting is made on Vacancies, by tagging an item. The list rests on
 * live postings; an ended one (tag removed, or the item filled) is under Inactive, with its
 * applicants kept.
 */
export function JobPostingsIndexPage() {
  const navigate = useNavigate();

  const fetchPostings = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      applicantsApi.listPostings(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          unit: query.facets.unit?.join(','),
        },
        signal,
      ),
    [],
  );

  return (
    <IndexPage<JobPostingListItem>
      module="applicants"
      breadcrumb={['Human Resources', 'Recruitment', 'Applicants']}
      columns={COLUMNS}
      facets={[
        { key: 'status', label: 'Status' },
        { key: 'unit', label: 'Office assignment' },
      ]}
      auditEntityTypes={APPLICANT_AUDIT_ENTITY_TYPES}
      rowKey={(posting) => posting.id}
      fetch={fetchPostings}
      searchPlaceholder="Search positions, item numbers or offices"
      emptyMessage="No job posting yet. Tag a vacant item for job posting under Vacancies."
      onRowOpen={(posting) => navigate(`/applicants/${posting.id}`)}
      openPermission="applicants.read"
      // The POSTING's provenance: when the item was tagged, and by whom.
      otherInfo={(posting) => posting}
    />
  );
}

/** The position title — and on the LIST, the item number joined to it, as on Vacancies. */
function PostingAnchor({ posting }: { posting: JobPostingListItem }) {
  const onList = useListSurface();

  return <TextCell value={onList ? `${posting.positionTitle} — ${posting.itemNumber}` : posting.positionTitle} />;
}

const COLUMNS: ColumnDef<JobPostingListItem>[] = [
  {
    key: 'positionTitle',
    header: 'Position title',
    width: 240,
    flex: true,
    sortable: true,
    render: (posting) => <PostingAnchor posting={posting} />,
  },
  {
    key: 'itemNumber',
    header: 'Item no.',
    width: 170,
    sortable: true,
    // On the list it rides in the anchor already.
    summary: false,
    render: (posting) => <TextCell value={posting.itemNumber} mono />,
  },
  {
    key: 'salaryGrade',
    header: 'Salary grade',
    width: 130,
    render: (posting) => <TextCell value={posting.salaryGradeLabel} />,
  },
  {
    key: 'monthlyAmount',
    header: 'Monthly salary',
    width: 140,
    align: 'right',
    render: (posting) => <TextCell value={formatMoney(posting.monthlyAmount)} mono />,
  },
  {
    key: 'orgUnitName',
    header: 'Office assignment',
    width: 200,
    sortable: true,
    summary: true,
    render: (posting) => <TextCell value={posting.orgUnitName} />,
  },
  {
    key: 'applicantCount',
    header: 'Applicants',
    width: 110,
    align: 'right',
    sortable: true,
    summary: true,
    render: (posting) => <TextCell value={String(posting.applicantCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (posting) =>
      <PostingStatusText posting={posting} />,
  },
];
